import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import cvData, { type ExperienceItem, type Bullet } from '../../data/cv';
import { periodSortKey, periodEndpoints, roleDuration, splitLocation } from '../cv/helpers';
import { PortfolioMark } from './PortfolioMark';
import { TechStack } from './TechPill';
function bulletText(b: Bullet): string {
  return typeof b === 'string' ? b : b.text;
}
function RoleCard({
  item,
  isLast
}: {
  item: ExperienceItem;
  isLast?: boolean;
}) {
  const {
    start,
    end,
    same
  } = periodEndpoints(item.period);
  const {
    place,
    mode
  } = splitLocation(item.location);
  const duration = roleDuration(item.period);
  const flatBullets: string[] = [...(item.bullets?.map(bulletText) ?? []), ...(item.subsections?.flatMap(s => s.bullets.map(bulletText)) ?? [])];
  return <motion.article initial={{
    opacity: 0,
    y: 48
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-80px'
  }} transition={{
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }} className="group/card relative grid grid-cols-[auto_1fr] gap-4 sm:gap-6 md:gap-8" data-id="element-252" data-mp="1od4otq6rh1ef">
      {/* rail node */}
      <div className="relative flex flex-col items-center" data-id="element-253" data-mp="25kku9froa89n">
        <div className="transition-transform duration-300 ease-out group-hover/card:-translate-y-0.5 sm:hidden" data-id="element-254" data-mp="29jadhoze6k17">
          <PortfolioMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} size={40} data-id="element-255" data-mp="1cvdru5p3kyj4" />
        </div>
        <div className="hidden transition-transform duration-300 ease-out group-hover/card:-translate-y-0.5 sm:block" data-id="element-256" data-mp="1lswk9v52mf51">
          <PortfolioMark company={item.company} mark={item.mark} domain={item.domain} logo={item.logo} data-id="element-257" data-mp="1qqy2yeyl4cfz" />
        </div>
      </div>

      <div className={isLast ? '' : 'pb-14'} data-id="element-258" data-mp="32ak9ft2bw517">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1" data-id="element-259" data-mp="125ggkic12pqy">
          <span className="font-mono text-[11px] tabular-nums text-stone-500" data-id="element-260" data-mp="y4mr3kixt2vy">
            {same ? end : `${start} — ${end}`}
          </span>
          {duration && <span className="rounded-full bg-[var(--accent)]/15 px-2 py-[1px] font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--accent)]" data-id="element-261" data-mp="1c3mekouprbqj">
              {duration}
            </span>}
          {mode && <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-stone-600" data-id="element-262" data-mp="egniaf7xo5t6">
              {mode}
            </span>}
        </div>

        <h3 className="mt-2 font-display text-[26px] font-medium leading-tight text-white transition-colors duration-300 ease-out [@media(hover:hover)]:group-hover/card:text-[var(--accent)] md:text-[30px]" data-id="element-263" data-mp="t74f1l9mi7jq">
          {item.company}
        </h3>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.12em] text-[var(--accent)]" data-id="element-264" data-mp="3rvthd2m3s270">
          {item.title}
          {place && <span className="ml-2 text-stone-600" data-id="element-265" data-mp="27cg7b23ac9pe">· {place}</span>}
        </p>

        {item.summary?.[0] && <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-stone-300" data-id="element-266" data-mp="2c5slsje9304f">
            {item.summary[0]}
          </p>}

        {flatBullets.length > 0 && <ul className="mt-4 grid max-w-3xl gap-2 sm:grid-cols-2" data-id="element-267" data-mp="22dms958coan4">
            {flatBullets.slice(0, 6).map((b, i) => <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed text-stone-400" data-id="element-268" data-mp="2q4ik22ct35ud" data-mpi={i}>
                <span className="mt-[9px] h-px w-2.5 shrink-0 bg-[var(--accent)]" aria-hidden="true" data-id="element-269" data-mp="2h3ax2gfqprfl" />
                <span data-id="element-270" data-mp="2m0u9dasvnu4w">{b}</span>
              </li>)}
          </ul>}

        {item.stack && item.stack.length > 0 && <TechStack stack={item.stack} data-id="element-271" data-mp="1an8ow2xyqgpp" />}
      </div>
    </motion.article>;
}
export function ExperienceTimeline() {
  const {
    experience,
    meta
  } = cvData;
  const roles = [...experience].sort((a, b) => periodSortKey(b.period) - periodSortKey(a.period));
  const detailed = roles.slice(0, meta.detailedRoleCount);
  const railRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: railRef,
    offset: ['start center', 'end center']
  });
  const railScale = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return <section id="work" className="relative mx-auto max-w-6xl px-6 py-20 md:px-12" data-id="element-272" data-mp="28uyf8ivebmgd">
      <SectionHeading index={meta.sections.experience.index} title={meta.sections.experience.title} data-id="element-273" data-mp="23xg20gacil2k" />

      <div ref={railRef} className="relative mt-14" data-id="element-274" data-mp="1zww43ahy7dfd">
        {/* animated progress rail behind the marks (left of the grid) */}
        <div aria-hidden="true" className="absolute bottom-6 left-[20px] top-2 w-px bg-white/10 sm:left-[26px]" data-id="element-275" data-mp="ogfpfje7x0tk">
          <motion.div style={{
          scaleY: railScale
        }} className="h-full w-full origin-top bg-[var(--accent)]" data-id="element-276" data-mp="jfi4x05qjuow" />
        </div>

        <div className="space-y-2" data-id="element-277" data-mp="30417u1sjwry1">
          {detailed.map((item, i) => <RoleCard key={i} item={item} isLast={i === detailed.length - 1} data-id="element-278" data-mp="344la1jrzelr1" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}
export function SectionHeading({
  index,
  title
}: {
  index: string;
  title: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.5
  }} className="flex items-baseline gap-4 border-b border-white/12 pb-3" data-id="element-279" data-mp="3duek2gch6st6">
      <span className="font-mono text-[12px] tabular-nums text-[var(--accent)]" data-id="element-280" data-mp="3rs30nbzifdsf">
        {index}
      </span>
      <h2 className="font-display text-[clamp(28px,5vw,44px)] font-light tracking-tight text-white" data-id="element-281" data-mp="1xg4tdfkrgrz6">
        {title}
      </h2>
    </motion.div>;
}